import React from 'react';

interface AgencyLogoProps {
  name: string;
  acronym?: string;
  size?: number;
}

// Keyed by actual DB acronyms
const AGENCY_COLORS: Record<string, string> = {
  'NYPD':      '#1d4ed8',
  'FDNY':      '#b91c1c',
  'DOHMH':     '#0d7377',
  'DOT':       '#b45309',
  'DEP':       '#065f46',
  'MTA':       '#0f766e',
  'DOB':       '#6d28d9',
  'DSNY':      '#15803d',
  'HRA':       '#0369a1',
  'ConEd':     '#d97706',
  'DOE':       '#be185d',
  'DHSES':     '#374151',
  'NYSP':      '#1e40af',
  'NYSDOH':    '#0e7490',
  'FEMA R2':   '#7c3aed',
  'USCG NY':   '#1e3a5f',
  'DHS/FEMA':  '#92400e',
  'NYCEM':     '#222B4E',
};

// Full name → acronym, keyed by actual DB agency names
const AGENCY_ACRONYMS: Record<string, string> = {
  'New York City Police Department':                                  'NYPD',
  'Fire Department of New York':                                      'FDNY',
  'NYC Department of Health and Mental Hygiene':                      'DOHMH',
  'NYC Department of Transportation':                                 'DOT',
  'NYC Department of Environmental Protection':                       'DEP',
  'Metropolitan Transportation Authority':                            'MTA',
  'NYC Department of Buildings':                                      'DOB',
  'NYC Department of Sanitation':                                     'DSNY',
  'NYC Human Resources Administration':                               'HRA',
  'Con Edison':                                                       'ConEd',
  'NYC Department of Education':                                      'DOE',
  'New York State Division of Homeland Security and Emergency Services': 'DHSES',
  'New York State Police':                                            'NYSP',
  'New York State Department of Health':                              'NYSDOH',
  'Federal Emergency Management Agency Region 2':                    'FEMA R2',
  'United States Coast Guard Sector New York':                       'USCG NY',
  'Department of Homeland Security / FEMA National Response Coordination': 'DHS/FEMA',
  'NYC Emergency Management':                                         'NYCEM',
};

function getAcronym(name: string): string {
  const known = AGENCY_ACRONYMS[name];
  if (known) return known;
  return name
    .split(' ')
    .filter((w) => w.length > 2)
    .map((w) => w[0].toUpperCase())
    .slice(0, 4)
    .join('');
}

// Shorten known long acronyms so they fit at small display sizes
const DISPLAY_OVERRIDES: Record<string, string> = {
  'DOHMH':  'DOH',
  'DHSES':  'DHS',
  'NYSDOH': 'DOH',
  'NYCEM':  'OEM',
};

// Split on space/slash, take first segment, cap at 4 chars, apply overrides
function getDisplayAcr(acr: string): string {
  const override = DISPLAY_OVERRIDES[acr];
  if (override) return override;
  const first = acr.split(/[\s/]/)[0];
  return first.length > 4 ? first.slice(0, 4) : first;
}

function getColor(acr: string): string {
  const known = AGENCY_COLORS[acr];
  if (known) return known;
  const palette = [
    '#2B358E', '#1d4ed8', '#0d7377',
    '#15803d', '#b45309', '#6d28d9',
    '#0369a1', '#065f46', '#92400e',
  ];
  let hash = 0;
  for (const c of acr) {
    hash = (hash * 31 + c.charCodeAt(0)) % palette.length;
  }
  return palette[Math.abs(hash)];
}

function getFontSize(size: number, acLength: number): number {
  const scale =
    acLength <= 2 ? 0.38 :
    acLength === 3 ? 0.32 :
    acLength === 4 ? 0.26 :
    0.22;
  return Math.max(Math.floor(size * scale), 7);
}

export function AgencyLogo({ name, acronym, size = 32 }: AgencyLogoProps) {
  const acr        = acronym || getAcronym(name);
  const displayAcr = getDisplayAcr(acr);
  const color      = getColor(acr);
  const fontSize   = getFontSize(size, displayAcr.length);

  return (
    <div
      title={name}
      style={{
        width:          size,
        height:         size,
        minWidth:       size,
        minHeight:      size,
        borderRadius:   Math.round(size * 0.22),
        background:     color,
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'center',
        fontWeight:     800,
        fontSize,
        color:          'white',
        letterSpacing:  displayAcr.length >= 4 ? '-0.5px' : '0px',
        flexShrink:     0,
        overflow:       'hidden',
        whiteSpace:     'nowrap',
        lineHeight:     1,
        textAlign:      'center',
        userSelect:     'none',
        fontFamily:     'inherit',
      }}
    >
      {displayAcr}
    </div>
  );
}

export default AgencyLogo;
